import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDownIcon } from "../icons/icons";

// Import graph components
import BodyFatGraph from "./graphs/BodyFatGraph";
import BloodSugarGraph from "./graphs/BloodSugarGraph";
import ReactionSpeedGraph from "./graphs/ReactionSpeedGraph";
import AdvancedHeartGraph from "./graphs/AdvancedHeartGraph";
import MedicationReminderGraph from "./graphs/MedicationReminderGraph";
import HealthAlertsGraph from "./graphs/HealthAlertsGraph";
import AllergySensitivityGraph from "./graphs/AllergySensitivityGraph";
import HormonalCycleGraph from "./graphs/HormonalCycleGraph";
import BloodPressureGraph from "./graphs/BloodPressureGraph";
import HydrationGraph from "./graphs/HydrationGraph";
import ReadinessScoreGraph from "./graphs/ReadinessScoreGraph";
import SkinTemperatureGraph from "./graphs/SkinTemperatureGraph";
import BioAgeGraph from "./graphs/BioAgeGraph";
import MoodTrackingGraph from "./graphs/MoodTrackingGraph";
import FoodInsightsGraph from "./graphs/FoodInsightsGraph";
import WorkoutModeGraph from "./graphs/WorkoutModeGraph";
interface HealthFeature {
    id: string;
    title: string;
    value: string;
    unit: string;
    subtitle?: string;
    icon: React.FC<{ className?: string }>;
    iconClass: string;
    textClass: string;
    hasGraph: boolean;
    graphType?: string;
    tooltip?: string | React.ReactNode;
    expandable: boolean;
    hasBadge?: boolean;
    deep?: string;
    light?: string;
}

interface ExpandableCardProps {
    feature: HealthFeature;
    isOpen: boolean;
    onToggle: () => void;
}

const ExpandableCard: React.FC<ExpandableCardProps> = ({ feature, isOpen, onToggle }) => {
    return (
        <motion.div
            className={`bg-white/90 p-6 h-full rounded-[24px] border border-white/20 backdrop-blur-sm shadow-[0_8px_24px_rgba(0,0,0,0.04)] relative overflow-hidden ${
                isOpen ? "row-span-2 col-span-1" : ""
            }`}
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
                duration: 0.3,
                layout: {
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                    duration: 0.5,
                },
            }}
            onClick={onToggle}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
        >
            {/* Soft gradient background */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-br opacity-10 -z-10"
                style={{
                    background: `radial-gradient(circle at 30% 20%, ${
                        feature.id.includes("stress") ? "rgba(0, 229, 255, 0.2)" : feature.id.includes("heart") ? "rgba(0, 114, 206, 0.2)" : "rgba(167, 255, 235, 0.2)"
                    }, transparent 70%)`,
                }}
                animate={{
                    opacity: [0.1, 0.15, 0.1],
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: [0.4, 0.0, 0.2, 1],
                }}
            />

            {/* Subtle noise overlay */}
            <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.02] mix-blend-overlay"></div>

            <div className="flex items-start gap-3 text-start">
                {/* آیکون باکس */}
                {/* تغییر ۱: حذف ml-3 (چون gap-3 در والد کارش را انجام می‌دهد) */}
                <div className={`bg-[#ECEFF1] rounded-[16px] w-12 h-12 flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.02)] relative shrink-0`}>
                    <feature.icon />
                    {feature.hasBadge && (
                        <div className="absolute -bottom-3 bg-gradient-to-r from-[#0072CE]/70 to-[#00E5FF]/70 text-[8px] text-white px-2 py-1 rounded-full shadow-sm backdrop-blur-sm font-medium whitespace-nowrap">
                            به‌زودی
                        </div>
                    )}
                </div>

                {/* محتوا */}
                <div className="flex-grow">
                    <div className="flex justify-between items-center mb-1">
                        <h3 className="font-medium text-[#002B5C]">{feature.title}</h3>
                        {feature.expandable && (
                            <button className="text-[#9EA3A9] hover:text-[#0072CE] transition-colors">
                                <ChevronDownIcon isOpen={isOpen} />
                            </button>
                        )}
                    </div>

                    <div className="flex items-baseline" >

                        <span className={`text-2xl font-bold text-[#0072CE]`}>{feature.value}</span>

                        {/* تغییر ۲: تبدیل ml-1 به ms-1 (Margin Start) */}
                        <span className="ms-1 text-sm text-[#9EA3A9]">{feature.unit}</span>
                    </div>

                </div>
            </div>
            {/* Expanded content */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{
                            opacity: 1,
                            height: "auto",
                            marginTop: "1rem",
                            transition: {
                                opacity: { duration: 0.3, delay: 0.2 },
                                height: {
                                    duration: 0.5,
                                    ease: [0.4, 0.0, 0.2, 1],
                                },
                                marginTop: { duration: 0.3 },
                            },
                        }}
                        exit={{
                            opacity: 0,
                            height: 0,
                            marginTop: 0,
                            transition: {
                                opacity: { duration: 0.2 },
                                height: { duration: 0.4 },
                                marginTop: { duration: 0.2 },
                            },
                        }}
                    >
                        <div className="text-[#002B5C] text-sm leading-relaxed">
                            <p className="mb-3">{feature.tooltip}</p>

                            {/* Daily Activity expanded content */}
                            {feature.id === "daily-activity" && (
                                <div className="space-y-3">
                                    <div>
                                        <h4 className="font-medium text-[#002B5C] mb-1">جزئیات فعالیت</h4>
                                        <div className="grid grid-cols-3 gap-3 mb-3">
                                            <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                                                <div className="text-lg font-medium text-[#32D74B]">۴۲۳</div>
                                                <div className="text-xs text-[#9EA3A9]">کالری</div>
                                            </div>
                                            <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                                                <div className="text-lg font-medium text-[#32D74B]">۴۷</div>
                                                <div className="text-xs text-[#9EA3A9]">دقیقه فعال</div>
                                            </div>
                                            <div className="bg-[#ECEFF1] p-3 rounded-[16px] text-center shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                                                <div className="text-lg font-medium text-[#32D74B]">۱۲</div>
                                                <div className="text-xs text-[#9EA3A9]">طبقه</div>
                                            </div>
                                        </div>

                                        <div className="text-xs text-[#9EA3A9] mb-2">پیشرفت روزانه</div>
                                        <div className="w-full bg-[#ECEFF1] rounded-full h-2 mb-4">
                                            <motion.div
                                                className="bg-[#32D74B] h-2 rounded-full"
                                                initial={{ width: 0 }}
                                                animate={{ width: "84%" }}
                                                transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
                                            />
                                        </div>

                                        <h4 className="font-medium text-[#002B5C] mb-1">روند هفتگی</h4>
                                        <div className="h-20 bg-[#ECEFF1] rounded-[16px] p-3 flex items-end justify-between shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                                            {["ش", "ی", "د", "س", "چ", "پ", "ج"].map((day, i) => {
                                                const heights = [60, 45, 80, 30, 65, 90, 84];
                                                return (
                                                    <div key={i} className="flex flex-col items-center">
                                                        <motion.div
                                                            className="w-4 bg-[#32D74B] rounded-t-[4px]"
                                                            initial={{ height: 0 }}
                                                            animate={{ height: `${heights[i]}%` }}
                                                            transition={{
                                                                duration: 0.2,
                                                                delay: i * 0.05,
                                                                ease: [0.4, 0.0, 0.2, 1],
                                                            }}
                                                        />
                                                        <span className="text-xs text-[#9EA3A9] mt-1">{day}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Stress Management expanded content */}
                            {feature.id === "stress-management" && (
                                <div className="space-y-3">
                                    <div>
                                        <h4 className="font-medium text-[#002B5C] mb-1">سطح استرس امروز</h4>
                                        <div className="flex items-center justify-between mb-1">
                                            <span>صبح</span>
                                            <span className="font-medium text-[#0072CE]">کم</span>
                                        </div>
                                        <div className="w-full bg-[#ECEFF1] rounded-full h-2 mb-2">
                                            <motion.div
                                                className="bg-gradient-to-r from-[#0072CE] to-[#00E5FF] h-2 rounded-full"
                                                initial={{ width: 0 }}
                                                animate={{ width: "25%" }}
                                                transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
                                            />
                                        </div>

                                        <div className="flex items-center justify-between mb-1">
                                            <span>ظهر</span>
                                            <span className="font-medium text-[#0072CE]">متوسط</span>
                                        </div>
                                        <div className="w-full bg-[#ECEFF1] rounded-full h-2 mb-2">
                                            <motion.div
                                                className="bg-gradient-to-r from-[#0072CE] to-[#00E5FF] h-2 rounded-full"
                                                initial={{ width: 0 }}
                                                animate={{ width: "55%" }}
                                                transition={{
                                                    duration: 0.2,
                                                    delay: 0.05,
                                                    ease: [0.4, 0.0, 0.2, 1],
                                                }}
                                            />
                                        </div>

                                        <div className="flex items-center justify-between mb-1">
                                            <span>عصر</span>
                                            <span className="font-medium text-[#0072CE]">کم</span>
                                        </div>
                                        <div className="w-full bg-[#ECEFF1] rounded-full h-2 mb-4">
                                            <motion.div
                                                className="bg-gradient-to-r from-[#0072CE] to-[#00E5FF] h-2 rounded-full"
                                                initial={{ width: 0 }}
                                                animate={{ width: "30%" }}
                                                transition={{
                                                    duration: 0.2,
                                                    delay: 0.1,
                                                    ease: [0.4, 0.0, 0.2, 1],
                                                }}
                                            />
                                        </div>

                                        <h4 className="text-sm font-medium text-[#002B5C] mb-2">جلسات مدیتیشن</h4>
                                        <div className="flex items-center space-x-2-reverse mb-1 flex-row-reverse">
                                            <svg className="w-4 h-4 text-[#32D74B]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                            </svg>
                                            <span className="text-sm">صبح: ۴۵ دقیقه</span>
                                        </div>
                                        <div className="flex items-center space-x-2-reverse flex-row-reverse">
                                            <svg className="w-4 h-4 text-[#32D74B]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                            </svg>
                                            <span className="text-sm">عصر: ۷۵ دقیقه</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Body Fat */}
                            {feature.id === "body-fat" && <BodyFatGraph />}

                            {/* Blood Sugar */}
                            {feature.id === "blood-sugar" && <BloodSugarGraph />}

                            {/* Reaction Speed */}
                            {feature.id === "reaction-speed" && <ReactionSpeedGraph />}

                            {/* Advanced Heart */}
                            {feature.id === "advanced-heart" && <AdvancedHeartGraph />}

                            {/* Medication Reminder */}
                            {feature.id === "medication-reminder" && <MedicationReminderGraph />}

                            {/* Health Alerts */}
                            {feature.id === "health-alerts" && <HealthAlertsGraph />}

                            {/* Allergy Sensitivity */}
                            {feature.id === "allergy-sensitivity" && <AllergySensitivityGraph />}

                            {/* Hormonal Cycle */}
                            {feature.id === "hormonal-cycle" && <HormonalCycleGraph />}

                            {/* Blood Pressure */}
                            {feature.id === "blood-pressure" && <BloodPressureGraph />}

                            {/* Hydration */}
                            {feature.id === "hydration" && <HydrationGraph />}
                            {/* Readiness Score */}
                            {feature.id === "readiness-score" && <ReadinessScoreGraph />}

                            {/* Skin Temperature */}
                            {feature.id === "skin-temperature" && <SkinTemperatureGraph />}

                            {/* Bio Age */}
                            {feature.id === "bio-age" && <BioAgeGraph />}

                            {/* Mood Tracking */}
                            {feature.id === "mood-tracking" && <MoodTrackingGraph />}

                            {/* Food Insights */}
                            {feature.id === "food-insights" && <FoodInsightsGraph />}

                            {/* Workout Mode */}
                            {feature.id === "workout-mode" && <WorkoutModeGraph />}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default ExpandableCard;
